import { useEffect, useState } from "react";

/**
 * Cloudflare Turnstile helper — Rev 26.5.
 *
 * Renders the widget once into a caller-provided container, captures
 * the token into module-level shared state, and exposes a `useTurnstileToken`
 * hook so both CopilotComposer + ChatWidget can read the same token (one
 * challenge per page-load, not one per chat surface).
 *
 * Design contract:
 *   - Site key sourced from VITE_TURNSTILE_SITE_KEY (baked at build
 *     time by `pnpm deploy:web` after pulling from SSM).
 *   - Backend treats the token as OPTIONAL: missing/invalid → no bump
 *     (Rev 26.4 baseline limits). The widget failing to mount is fine —
 *     visitor just sees the un-bumped limits.
 *   - Managed mode + appearance:"interaction-only" → invisible ~95%
 *     of the time. CF decides when to challenge based on risk model.
 */

type TurnstileWidget = {
  render: (
    selector: string | HTMLElement,
    opts: TurnstileRenderOpts,
  ) => string;
  reset: (widgetId?: string) => void;
  getResponse: (widgetId?: string) => string | undefined;
};

type TurnstileRenderOpts = {
  sitekey: string;
  size?: "normal" | "flexible" | "compact";
  appearance?: "always" | "execute" | "interaction-only";
  callback?: (token: string) => void;
  "expired-callback"?: () => void;
  "error-callback"?: () => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileWidget;
  }
}

const SITE_KEY = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) ?? "";

// Module-level state so multiple components see the same token.
let sharedToken: string | null = null;
const subscribers = new Set<(t: string | null) => void>();
function setShared(t: string | null) {
  sharedToken = t;
  subscribers.forEach((cb) => cb(t));
}

// Rev 27 Loop C — separate verified-or-not state so consumers can swap
// UI without needing to re-derive `verified = !!token` (which would
// trigger a render between mount and the first non-null token write).
let sharedVerified = false;
const verifiedSubs = new Set<(v: boolean) => void>();
function setSharedVerified(v: boolean) {
  sharedVerified = v;
  verifiedSubs.forEach((cb) => cb(v));
}

export function useTurnstileToken(): string | null {
  const [token, setToken] = useState<string | null>(sharedToken);
  useEffect(() => {
    subscribers.add(setToken);
    return () => {
      subscribers.delete(setToken);
    };
  }, []);
  return token;
}

export function useTurnstileVerified(): boolean {
  const [v, setV] = useState(sharedVerified);
  useEffect(() => {
    verifiedSubs.add(setV);
    return () => {
      verifiedSubs.delete(setV);
    };
  }, []);
  return v;
}

let mounted = false;

/**
 * Render the widget into the provided container. Idempotent — only
 * the first call actually mounts; subsequent calls are no-ops so
 * multiple chat surfaces (composer + widget) sharing the same hook
 * don't double-mount.
 *
 * If VITE_TURNSTILE_SITE_KEY is empty (dev build without the env)
 * or window.turnstile isn't loaded yet, this is a silent no-op.
 * The caller should pair this with `whenTurnstileReady` so the
 * script-load race resolves cleanly.
 */
export function mountTurnstile(container: HTMLElement | null): void {
  if (mounted) return;
  if (!container) return;
  if (!SITE_KEY) return;
  if (typeof window === "undefined" || !window.turnstile) return;
  mounted = true;
  window.turnstile.render(container, {
    sitekey: SITE_KEY,
    size: "flexible",
    appearance: "interaction-only",
    callback: (t: string) => {
      setShared(t);
      setSharedVerified(true);
    },
    "expired-callback": () => {
      setShared(null);
      setSharedVerified(false);
    },
    "error-callback": () => {
      setShared(null);
      setSharedVerified(false);
    },
  });
}

/**
 * Poll window.turnstile readiness. Calls `cb` once when the runtime
 * is available; gives up after 10 s. Use to dodge the async-script
 * race without a load event listener (the script tag uses defer +
 * async, so neither DOMContentLoaded nor load fires reliably for it).
 */
export function whenTurnstileReady(cb: () => void): void {
  if (typeof window === "undefined") return;
  if (window.turnstile) {
    cb();
    return;
  }
  const id = window.setInterval(() => {
    if (window.turnstile) {
      window.clearInterval(id);
      cb();
    }
  }, 100);
  window.setTimeout(() => window.clearInterval(id), 10_000);
}

export const TURNSTILE_SITE_KEY = SITE_KEY;
